"use client";

import * as React from "react";

/**
 * A `<select>` with a `value` prop that keeps its value through a form reset.
 *
 * React 19 calls `form.reset()` after a `<form action={...}>` action finishes,
 * and a reset puts every option back to its `selected` ATTRIBUTE
 * (`defaultSelected`). React sets that attribute only for `defaultValue`, never
 * for `value`, so a controlled select jumped to its first option ("Select a
 * city...") after every save while the component state still held the real
 * value. React then saw no state change and never put it back, and a `required`
 * select blocked the next submit.
 *
 * The fix: mirror `value` onto `defaultSelected` after every render, so a reset
 * restores the controlled value. The reset runs at the end of React's mutation
 * phase, before layout effects, so this effect also re-applies the selection
 * when the reset lands in the same commit as this render.
 */
export const ControlledSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function ControlledSelect({ children, ...props }, ref) {
    const inner = React.useRef<HTMLSelectElement | null>(null);
    const { value } = props;

    React.useImperativeHandle(ref, () => inner.current as HTMLSelectElement);

    React.useLayoutEffect(() => {
      const el = inner.current;
      if (!el || value === undefined || value === null) return;
      const wanted = new Set(Array.isArray(value) ? value.map(String) : [String(value)]);
      for (const option of Array.from(el.options)) {
        const on = wanted.has(option.value);
        if (option.defaultSelected !== on) option.defaultSelected = on;
        if (option.selected !== on) option.selected = on;
      }
    });

    return (
      <select ref={inner} {...props}>
        {children}
      </select>
    );
  },
);
